import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  addDoc,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase.config';
import { logger } from '../utils/logger';

// ==================== ROLE CONSTANTS ====================

export const ROLES = {
  ADMIN: 'admin',
  DOCTOR: 'doctor',
  MA: 'ma',
  GI_SPECIALIST: 'gi_specialist',
  PCP: 'pcp',
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.DOCTOR]: 'Doctor',
  [ROLES.MA]: 'Medical Assistant',
  [ROLES.GI_SPECIALIST]: 'GI Specialist',
  [ROLES.PCP]: 'PCP',
};

// Roles available in the AIGI admin portal (excludes GI Specialist and PCP which belong to other portals)
export const ADMIN_PORTAL_ROLES = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.DOCTOR]: 'Doctor',
  [ROLES.MA]: 'Medical Assistant',
};

// Portal -> default role mapping
export const PORTAL_ROLE_MAP = {
  gi: ROLES.GI_SPECIALIST,
  pcp: ROLES.PCP,
};

const APPOINTMENT_NOTIFICATION_DOC = doc(db, 'settings', 'appointment_notifications');
const AI_SUMMARY_TEMPLATE_DOC = doc(db, 'settings', 'ai_summary_template');

export const DEFAULT_AI_SUMMARY_TEMPLATE = `DEMOGRAPHICS AND CHIEF COMPLAINT:
Write one paragraph: patient full name, age, gender, DOB, presenting complaint, and BMI.

HISTORY OF PRESENT ILLNESS:
Write a detailed paragraph describing onset, duration, severity, character, aggravating/relieving factors, associated symptoms, and what the patient explicitly denied — based on chatbot interview.

PAST MEDICAL AND SURGICAL HISTORY:
List each medical condition and past surgery as a separate bullet point using "•" symbol.

CURRENT MEDICATIONS:
List each medication as a separate bullet point using "•" symbol. Include dosage and form if available.

ALLERGIES:
List each allergy as a separate bullet point using "•" symbol.

MOST LIKELY DIAGNOSIS AND DIFFERENTIAL DIAGNOSIS:
Write the most likely diagnosis followed by differential diagnoses as a paragraph — based on chatbot interview and medical profile.

INVESTIGATIONS SUGGESTED:
List each recommended investigation or test as a separate bullet point using "•" symbol.

THERAPIES SUGGESTED:
List each recommended therapy and treatment as a separate bullet point using "•" symbol.`;

const toTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') {
    return value > 1e12 ? value : value * 1000;
  }
  // Handle Firestore Timestamp objects
  if (value && typeof value === 'object' && typeof value.seconds === 'number') {
    return value.seconds * 1000;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Recursively converts Firestore Timestamp objects to ISO strings so they
// are safe to store in React state and render as JSX children.
const serializeFirestoreData = (data) => {
  if (data === null || data === undefined) return data;
  // Firestore Timestamp: has numeric seconds + nanoseconds (and possibly a type field)
  if (typeof data === 'object' && typeof data.seconds === 'number' && typeof data.nanoseconds === 'number') {
    return new Date(data.seconds * 1000).toISOString();
  }
  if (Array.isArray(data)) return data.map(serializeFirestoreData);
  if (typeof data === 'object') {
    const result = {};
    for (const key of Object.keys(data)) {
      result[key] = serializeFirestoreData(data[key]);
    }
    return result;
  }
  return data;
};

const getSessionSortTimestamp = (session = {}) =>
  toTimestamp(
    session.created_at ||
    session.created_on
  );

// ==================== USER OPERATIONS ====================

export const getUser = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? serializeFirestoreData({ id: docSnap.id, ...docSnap.data() }) : null;
  } catch (error) {
    logger.error('Error getting user:', error);
    throw error;
  }
};

export const getUserAppointmentEmails = async (userId) => {
  try {
    if (!userId) return [];
    const docRef = doc(db, 'users', userId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return [];
    const data = snap.data() || {};
    return Array.isArray(data.appointment_notification_emails)
      ? data.appointment_notification_emails
      : [];
  } catch (error) {
    logger.error('Error getting user appointment emails:', error);
    return [];
  }
};

export const setUserAppointmentEmails = async (userId, emails = []) => {
  try {
    if (!userId) throw new Error('userId is required');
    const normalized = Array.from(
      new Set((emails || []).map((email) => (email || '').trim().toLowerCase()).filter(Boolean))
    );
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, {
      appointment_notification_emails: normalized,
      appointment_notification_emails_updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error saving user appointment emails:', error);
    throw error;
  }
};

export const getUserByEmail = async (email) => {
  try {
    const q = query(collection(db, 'users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    logger.error('Error getting user by email:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, userData, { merge: true });
    return true;
  } catch (error) {
    logger.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    logger.error('Error deleting user:', error);
    throw error;
  }
};

// ==================== ADMIN OPERATIONS ====================

export const getAdminByEmail = async (email) => {
  try {
    const q = query(collection(db, 'admin_users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const adminDoc = querySnapshot.docs[0];
    return { id: adminDoc.id, ...adminDoc.data() };
  } catch (error) {
    logger.error('Error getting admin by email:', error);
    throw error;
  }
};

export const getAllAdmins = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'admin_users'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting all admins:', error);
    throw error;
  }
};

export const getAdminsByRole = async (role) => {
  try {
    const q = query(collection(db, 'admin_users'), where('role', '==', role));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error(`Error getting admins by role ${role}:`, error);
    throw error;
  }
};

// ==================== CHATBOT OPERATIONS ====================

export const createChatSession = async (sessionData) => {
  try {
    const now = new Date().toISOString();
    const normalizedUserIds = Array.from(
      new Set(
        [
          sessionData.user_id,
          ...(Array.isArray(sessionData.user_ids) ? sessionData.user_ids : [])
        ].filter(Boolean)
      )
    );
    const docRef = await addDoc(collection(db, 'chatbot'), {
      ...sessionData,
      user_ids: normalizedUserIds,
      created_at: now,
      updated_at: now
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating chat session:', error);
    throw error;
  }
};

export const getChatSessions = async (userId) => {
  try {
    const [byUserIdSnap, byUserIdsArraySnap] = await Promise.all([
      getDocs(query(collection(db, 'chatbot'), where('user_id', '==', userId))),
      getDocs(query(collection(db, 'chatbot'), where('user_ids', 'array-contains', userId)))
    ]);
    const allDocs = [...byUserIdSnap.docs, ...byUserIdsArraySnap.docs];
    const unique = allDocs.filter(
      (docSnap, index, arr) => index === arr.findIndex((item) => item.id === docSnap.id)
    );
    return unique.map((docSnap) => serializeFirestoreData({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    logger.error('Error getting chat sessions:', error);
    throw error;
  }
};

export const getChatSession = async (sessionId) => {
  try {
    const docRef = doc(db, 'chatbot', sessionId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? serializeFirestoreData({ id: docSnap.id, ...docSnap.data() }) : null;
  } catch (error) {
    logger.error('Error getting chat session:', error);
    throw error;
  }
};

export const updateChatSession = async (sessionId, sessionData) => {
  try {
    const docRef = doc(db, 'chatbot', sessionId);
    await updateDoc(docRef, {
      ...sessionData,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating chat session:', error);
    throw error;
  }
};

export const deleteChatSession = async (sessionId) => {
  try {
    const docRef = doc(db, 'chatbot', sessionId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    logger.error('Error deleting chat session:', error);
    throw error;
  }
};

// ==================== MEDICAL CONTENT OPERATIONS ====================

export const getMedicalContent = async (userId) => {
  try {
    const q = query(collection(db, 'medical_content'), where('user_id', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting medical content:', error);
    throw error;
  }
};

export const createMedicalContent = async (contentData) => {
  try {
    const docRef = await addDoc(collection(db, 'medical_content'), {
      ...contentData,
      created_at: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating medical content:', error);
    throw error;
  }
};

export const updateMedicalContent = async (contentId, contentData) => {
  try {
    const docRef = doc(db, 'medical_content', contentId);
    await updateDoc(docRef, contentData);
    return true;
  } catch (error) {
    logger.error('Error updating medical content:', error);
    throw error;
  }
};

export const deleteMedicalContent = async (contentId) => {
  try {
    const docRef = doc(db, 'medical_content', contentId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    logger.error('Error deleting medical content:', error);
    throw error;
  }
};

// ==================== INSURANCE OPERATIONS ====================

export const getInsuranceData = async (userId) => {
  try {
    const q = query(collection(db, 'insurance_data'), where('user_id', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => serializeFirestoreData({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting insurance data:', error);
    throw error;
  }
};

export const createInsuranceData = async (insuranceData) => {
  try {
    const docRef = await addDoc(collection(db, 'insurance_data'), {
      ...insuranceData,
      created_at: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating insurance data:', error);
    throw error;
  }
};

export const updateInsuranceData = async (insuranceId, insuranceData) => {
  try {
    const docRef = doc(db, 'insurance_data', insuranceId);
    await updateDoc(docRef, insuranceData);
    return true;
  } catch (error) {
    logger.error('Error updating insurance data:', error);
    throw error;
  }
};

// ==================== UTILITY FUNCTIONS ====================

export const getAllUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting all users:', error);
    throw error;
  }
};

export const searchUsers = async (searchTerm) => {
  try {
    // Note: Firestore doesn't support full-text search natively
    // This is a simple implementation - for production, consider using Algolia or similar
    const querySnapshot = await getDocs(collection(db, 'users'));
    const allUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return allUsers.filter(user => 
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phoneNumber?.includes(searchTerm)
    );
  } catch (error) {
    logger.error('Error searching users:', error);
    throw error;
  }
};

export const calculateAndUpdateBMI = async (userId, heightCm, weightLbs) => {
  try {
    const height = Number(heightCm);
    const weight = Number(weightLbs);
    if (!height || !weight || height <= 0 || weight <= 0) {
      await updateUser(userId, { bmi: '' });
      return null;
    }

    const heightInMeters = height / 100;
    const weightInKg = weight * 0.45359237;
    const bmi = Number((weightInKg / (heightInMeters * heightInMeters)).toFixed(2));
    await updateUser(userId, { bmi });
    return bmi;
  } catch (error) {
    logger.error('Error calculating BMI:', error);
    throw error;
  }
};

export const createAdmin = async (adminData) => {
  try {
    const docRef = await addDoc(collection(db, 'admin_users'), {
      ...adminData,
      role: adminData.role || ROLES.ADMIN,
      created_on: new Date().toISOString()
    });
    await updateDoc(doc(db, 'admin_users', docRef.id), { user_id: docRef.id });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating admin:', error);
    throw error;
  }
};

export const getAdmin = async (adminId) => {
  try {
    if (!adminId) return null;

    const byId = await getDoc(doc(db, 'admin_users', adminId));
    if (byId.exists()) return { id: byId.id, ...byId.data() };

    const q = query(collection(db, 'admin_users'), where('user_id', '==', adminId));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const adminDoc = snap.docs[0];
    return { id: adminDoc.id, ...adminDoc.data() };
  } catch (error) {
    logger.error('Error getting admin:', error);
    throw error;
  }
};

export const updateAdmin = async (adminId, adminData) => {
  try {
    const existing = await getAdmin(adminId);
    if (!existing?.id) throw new Error('Admin not found');
    await updateDoc(doc(db, 'admin_users', existing.id), {
      ...adminData,
      updated_on: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating admin:', error);
    throw error;
  }
};

export const getSessionsAssignedToDoctor = async (doctorId) => {
  try {
    const q = query(collection(db, 'chatbot'), where('assigned_doctor_id', '==', doctorId));
    const snap = await getDocs(q);
    return snap.docs.map(docSnap => serializeFirestoreData({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    logger.error('Error getting sessions assigned to doctor:', error);
    throw error;
  }
};

export const getUsersWithPagination = async (page = 1, perPage = 10, searchQuery = '') => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    let allUsers = querySnapshot.docs.map((docSnap) => ({ user_id: docSnap.id, ...docSnap.data(), id: docSnap.id }));

    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      allUsers = allUsers.filter((user) =>
        user.fullName?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        user.phoneNumber?.includes(searchQuery)
      );
    }

    const usersWithSessions = await Promise.all(
      allUsers.map(async (user) => {
        const userIdsToCheck = Array.from(new Set([user.id, user.user_id].filter(Boolean)));
        const sessionsById = await Promise.all(userIdsToCheck.map((id) => getChatSessions(id)));
        const sessions = sessionsById
          .flat()
          .filter((session, index, arr) =>
            index === arr.findIndex((item) => (item.session_id || item.id) === (session.session_id || session.id))
          );
        let latestSession = null;
        if (sessions.length > 0) {
          latestSession = sessions.reduce((latest, current) => {
            const latestDate = getSessionSortTimestamp(latest);
            const currentDate = getSessionSortTimestamp(current);
            return currentDate > latestDate ? current : latest;
          });
        }
        const latestSessionDate = latestSession?.created_at || latestSession?.created_on || null;
        return {
          ...user,
          latestSessionDate,
          sortTimestamp: toTimestamp(latestSessionDate || user.created_at || user.created_on),
          hasNewAppointment: !!latestSession,
          sessionIds: sessions.map((item) => item.session_id || item.id).filter(Boolean),
          appointmentsCount: sessions.length
        };
      })
    );

    const users = usersWithSessions
      .filter((u) => u.hasNewAppointment)
      .sort((a, b) => {
        return b.sortTimestamp - a.sortTimestamp;
      });

    const totalRecords = users.length;
    const totalPages = Math.ceil(totalRecords / perPage) || 1;
    const startIndex = (page - 1) * perPage;
    const paginatedUsers = users.slice(startIndex, startIndex + perPage);

    return {
      users: paginatedUsers,
      total_pages: totalPages,
      total_records: totalRecords,
      current_page: page,
      per_page: perPage
    };
  } catch (error) {
    logger.error('Error getting paginated users:', error);
    throw error;
  }
};

export const getIllnesses = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'illnesses'));
    if (!querySnapshot.empty) {
      const illnessNames = [];
      querySnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        if (Array.isArray(data.illnesses)) {
          data.illnesses.forEach((item) => {
            if (typeof item === 'string' && item.trim()) {
              illnessNames.push(item.trim());
            }
          });
        } else if (typeof data.name === 'string' && data.name.trim()) {
          illnessNames.push(data.name.trim());
        } else if (docSnap.id) {
          illnessNames.push(docSnap.id);
        }
      });

      if (illnessNames.length > 0) {
        return Array.from(new Set(illnessNames));
      }
    }
  } catch (error) {
    logger.error('Error getting illnesses:', error);
  }
  return [
    'Screening Colon',
    'Acid Reflux',
    'IBS',
    'Abdominal Pain',
    'Rectal Bleeding',
    'Change in Bowel Habits',
    'Diarrhea',
    'Nausea and Vomiting',
    'Difficulty Swallowing',
    'Constipation',
    'Fatty Liver',
    'Upper Endoscopy',
    "Crohn's Disease",
    'Ulcerative Colitis',
    'Anemia',
    'Jaundice',
    'Other Problem'
  ];
};

export const getUserSessions = async (userId) => {
  try {
    let user = await getUser(userId);
    if (!user) {
      const q = query(collection(db, 'users'), where('user_id', '==', userId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const userDoc = snap.docs[0];
        user = serializeFirestoreData({ id: userDoc.id, ...userDoc.data() });
      }
    }
    // Backfill: if the user document exists but is missing the user_id field,
    // patch it so future fallback queries work correctly.
    if (user && !user.user_id) {
      try {
        const userDocRef = doc(db, 'users', user.id);
        await updateDoc(userDocRef, { user_id: user.id });
        user.user_id = user.id;
      } catch (e) {
        // Non-critical — continue even if backfill fails
        logger.warn('Failed to backfill user_id for', user.id, e);
      }
    }
    let userIdsToCheck = [];
    if (user) {
      userIdsToCheck = Array.from(new Set([userId, user.id, user.user_id].filter(Boolean)));
    } else {
      // Fallback for migrated/legacy accounts where profile lookup may fail
      userIdsToCheck = Array.from(new Set([userId].filter(Boolean)));
    }
    const sessionsById = await Promise.all(userIdsToCheck.map((id) => getChatSessions(id)));
    const history = sessionsById
      .flat()
      .filter((session, index, arr) =>
        index === arr.findIndex((item) => (item.session_id || item.id) === (session.session_id || session.id))
      )
      .sort((a, b) => {
        return getSessionSortTimestamp(b) - getSessionSortTimestamp(a);
      });

    // If user record wasn't found initially, try again using all unique
    // user IDs found across sessions (covers new-user ID mismatches).
    if (!user && history.length > 0) {
      const sessionUserIds = Array.from(
        new Set(
          history
            .flatMap((h) => [h.user_id, ...(Array.isArray(h.user_ids) ? h.user_ids : [])])
            .filter(Boolean)
        )
      );
      for (const sid of sessionUserIds) {
        if (user) break;
        user = await getUser(sid);
        if (!user) {
          const q = query(collection(db, 'users'), where('user_id', '==', sid));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const userDoc = snap.docs[0];
            user = serializeFirestoreData({ id: userDoc.id, ...userDoc.data() });
          }
        }
      }
    }

    if (!user && history.length === 0) return null;

    // Collect every user ID we know about — try them all for insurance
    // to handle legacy / migrated accounts where user_id may differ across collections
    const sessionUserIds = history.slice(0, 10).map(h => h.user_id).filter(Boolean);
    const allInsuranceIds = Array.from(new Set(
      [user?.id, user?.user_id, userId, ...sessionUserIds].filter(Boolean)
    ));
    let insurance = [];
    for (const uid of allInsuranceIds) {
      try {
        const result = await getInsuranceData(uid);
        if (result && result.length > 0) {
          insurance = result;
          break;
        }
      } catch (e) {
        // continue to next ID
      }
    }

    return serializeFirestoreData({
      user_details: user || {},
      history,
      insurance_data: insurance,
      bmi: user?.bmi || null,
      session_status: history.length > 0
    });
  } catch (error) {
    logger.error('Error getting user sessions:', error);
    throw error;
  }
};

export const createAdminLog = async (logData) => {
  try {
    const docRef = await addDoc(collection(db, 'admin_logs'), {
      ...logData,
      created_at: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating admin log:', error);
    throw error;
  }
};

export const getAdminViewedSessionIds = async (patientId, adminId = null) => {
  try {
    if (!patientId) return [];
    const logsQuery = adminId
      ? query(
        collection(db, 'admin_logs'),
        where('patient_id', '==', patientId),
        where('admin_id', '==', adminId)
      )
      : query(collection(db, 'admin_logs'), where('patient_id', '==', patientId));

    const snap = await getDocs(logsQuery);
    const viewedIds = snap.docs
      .map((docSnap) => (docSnap.data() || {}).session_id)
      .filter(Boolean);
    return Array.from(new Set(viewedIds));
  } catch (error) {
    logger.error('Error getting admin viewed session ids:', error);
    return [];
  }
};

export const getAdminViewedMapForPatients = async (patientIds = [], adminId = null) => {
  try {
    const uniquePatientIds = Array.from(new Set((patientIds || []).filter(Boolean)));
    const initial = uniquePatientIds.reduce((acc, id) => {
      acc[id] = [];
      return acc;
    }, {});
    if (uniquePatientIds.length === 0) return initial;

    const logsQuery = adminId
      ? query(collection(db, 'admin_logs'), where('admin_id', '==', adminId))
      : collection(db, 'admin_logs');
    const snap = await getDocs(logsQuery);

    const patientIdSet = new Set(uniquePatientIds);
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data() || {};
      if (!patientIdSet.has(data.patient_id) || !data.session_id) return;
      initial[data.patient_id].push(data.session_id);
    });

    Object.keys(initial).forEach((patientId) => {
      initial[patientId] = Array.from(new Set(initial[patientId]));
    });

    return initial;
  } catch (error) {
    logger.error('Error getting admin viewed map for patients:', error);
    return {};
  }
};

const normalizeFollowup = (item) => ({
  ...item,
  followup_id: item.followup_id || item.id,
  followup_PoC: item.followup_PoC ?? item.followup_poc ?? '',
  followup_CD: item.followup_CD ?? item.followup_cd ?? ''
});

export const getAllFollowupRequests = async (sessionId) => {
  try {
    const q = query(collection(db, 'followup_requests'), where('session_id', '==', sessionId));
    const snap = await getDocs(q);
    return snap.docs.map((docSnap) => normalizeFollowup(serializeFirestoreData({ id: docSnap.id, ...docSnap.data() })));
  } catch (error) {
    logger.error('Error getting followup requests:', error);
    throw error;
  }
};

export const getActiveFollowupRequest = async (sessionId) => {
  try {
    const q = query(
      collection(db, 'followup_requests'),
      where('session_id', '==', sessionId),
      where('status', '==', 'active')
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // Sort client-side by created_at desc to get the latest followup (no index required)
    const docs = snap.docs
      .map(d => serializeFirestoreData({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    const data = normalizeFollowup(docs[0]);
    return { ...data, activeFlag: data.status === 'active', data };
  } catch (error) {
    logger.error('Error getting active followup request:', error);
    throw error;
  }
};

export const getMedicalReports = async (reportsId, userId = null) => {
  try {
    if (!reportsId) return [];

    // Build query — add user_id filter when provided (required for patient users
    // to satisfy Firestore ownership rule: resource.data.user_id == request.auth.uid)
    const constraints = [where('reports_id', '==', reportsId)];
    if (userId) {
      constraints.push(where('user_id', '==', userId));
    }
    const q = query(collection(db, 'medical_reports'), ...constraints);
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        const normalizedDocType = data.doc_type === 'upload_report' ? 'other_report' : data.doc_type;
        return {
          id: docSnap.id,
          ...data,
          doc_type: normalizedDocType,
          doc_id: data.doc_id || docSnap.id,
          doc_summary: data.doc_summary || data.doc_text || data.text || data.file_name || null,
          doc_text: data.doc_text || data.text || '',
          doc_url: data.doc_url || data.file_url || null
        };
      });
    }

    const legacyDoc = await getDoc(doc(db, 'medical_reports', reportsId));
    if (!legacyDoc.exists()) return [];
    const data = legacyDoc.data() || {};
    if (Array.isArray(data.content)) return data.content;
    return [{ id: legacyDoc.id, ...data }];
  } catch (error) {
    logger.error('Error getting medical reports:', error);
    throw error;
  }
};

export const createMedicalReport = async (reportData) => {
  try {
    const docRef = await addDoc(collection(db, 'medical_reports'), {
      ...reportData,
      created_at: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating medical report:', error);
    throw error;
  }
};

export const updateMedicalReportSummary = async (reportId, summary) => {
  try {
    await updateDoc(doc(db, 'medical_reports', reportId), {
      doc_summary: summary,
      summarized_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating medical report summary:', error);
    return false;
  }
};

export const getFollowupReports = async (followupId) => {
  try {
    const q = query(
      collection(db, 'medical_reports'),
      where('followup_id', '==', followupId)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    logger.error('Error fetching followup reports:', error);
    return [];
  }
};

export const upsertPCPPharmacyDetails = async (reportsId, details) => {
  try {
    if (!reportsId) throw new Error('reportsId is required');
    await setDoc(
      doc(db, 'pcp_pharmacy', reportsId),
      {
        reports_id: reportsId,
        user_id: details?.user_id || '',
        pcp_name: details?.pcp?.name || '',
        pcp_phone: details?.pcp?.phone || '',
        pcp_fax: details?.pcp?.fax || '',
        pcp_address: details?.pcp?.address || '',
        pcp_email: details?.pcp?.email || '',
        pharmacy_name: details?.pharmacy?.name || '',
        pharmacy_phone: details?.pharmacy?.phone || '',
        pharmacy_fax: details?.pharmacy?.fax || '',
        pharmacy_address: details?.pharmacy?.address || '',
        pharmacy_email: details?.pharmacy?.email || '',
        updated_at: new Date().toISOString()
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    logger.error('Error upserting PCP/Pharmacy details:', error);
    throw error;
  }
};

// ==================== SETTINGS ====================

export const getAppointmentNotificationEmails = async () => {
  try {
    const snap = await getDoc(APPOINTMENT_NOTIFICATION_DOC);
    if (!snap.exists()) return [];
    const data = snap.data() || {};
    return Array.isArray(data.emails) ? data.emails : [];
  } catch (error) {
    logger.error('Error getting appointment notification emails:', error);
    return [];
  }
};

export const setAppointmentNotificationEmails = async (emails, metadata = {}) => {
  try {
    const normalized = Array.from(
      new Set((emails || []).map((email) => (email || '').trim().toLowerCase()).filter(Boolean))
    );
    await setDoc(
      APPOINTMENT_NOTIFICATION_DOC,
      {
        emails: normalized,
        updated_at: new Date().toISOString(),
        updated_by: metadata.adminId || '',
        updated_by_email: metadata.adminEmail || '',
        updated_by_name: metadata.adminName || ''
      },
      { merge: true }
    );
    return true;
  } catch (error) {
    logger.error('Error saving appointment notification emails:', error);
    throw error;
  }
};

export const getAISummaryTemplate = async () => {
  try {
    const snap = await getDoc(AI_SUMMARY_TEMPLATE_DOC);
    if (!snap.exists()) return null;
    return snap.data().template || null;
  } catch (error) {
    logger.error('Error getting AI summary template:', error);
    return null;
  }
};

export const setAISummaryTemplate = async (template) => {
  try {
    await setDoc(AI_SUMMARY_TEMPLATE_DOC, { template, updated_at: new Date().toISOString() }, { merge: true });
    return true;
  } catch (error) {
    logger.error('Error saving AI summary template:', error);
    throw error;
  }
};

export const getPCPPharmacyDetails = async (reportsId) => {
  try {
    if (!reportsId) return null;
    const snap = await getDoc(doc(db, 'pcp_pharmacy', reportsId));
    if (!snap.exists()) return null;
    const data = { id: snap.id, ...snap.data() };
    return {
      ...data,
      pcp: {
        name: data.pcp_name || '',
        phone: data.pcp_phone || '',
        fax: data.pcp_fax || '',
        address: data.pcp_address || ''
      },
      pharmacy: {
        name: data.pharmacy_name || '',
        phone: data.pharmacy_phone || '',
        fax: data.pharmacy_fax || '',
        address: data.pharmacy_address || ''
      }
    };
  } catch (error) {
    logger.error('Error getting PCP/Pharmacy details:', error);
    return null;
  }
};

export const getPCPPharmacyByUserId = async (userId) => {
  try {
    if (!userId) return null;
    const q = query(collection(db, 'pcp_pharmacy'), where('user_id', '==', userId));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // Get the most recently updated document
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const data = docs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
    return {
      ...data,
      pcp: {
        name: data.pcp_name || '',
        phone: data.pcp_phone || '',
        fax: data.pcp_fax || '',
        address: data.pcp_address || ''
      },
      pharmacy: {
        name: data.pharmacy_name || '',
        phone: data.pharmacy_phone || '',
        fax: data.pharmacy_fax || '',
        address: data.pharmacy_address || ''
      }
    };
  } catch (error) {
    logger.error('Error getting PCP/Pharmacy by userId:', error);
    return null;
  }
};

export const updateFollowupCD = async (followupId, conditionalDiagnosis) => {
  try {
    await updateDoc(doc(db, 'followup_requests', followupId), {
      followup_cd: conditionalDiagnosis,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating followup CD:', error);
    throw error;
  }
};

export const updateFollowupPoC = async (followupId, planOfCare) => {
  try {
    await updateDoc(doc(db, 'followup_requests', followupId), {
      followup_poc: planOfCare,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating followup PoC:', error);
    throw error;
  }
};

export const updateFollowupSummary = async (followupId, summaryData) => {
  try {
    await updateDoc(doc(db, 'followup_requests', followupId), {
      ...summaryData,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating followup summary:', error);
    throw error;
  }
};

export const updateFollowupReportIds = async (followupId, reportIds) => {
  try {
    await updateDoc(doc(db, 'followup_requests', followupId), {
      uploaded_report_ids: reportIds,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating followup report IDs:', error);
    throw error;
  }
};

export const updateSessionMainSummary = async (sessionId, summary) => {
  try {
    await updateDoc(doc(db, 'chatbot', sessionId), {
      main_summary: summary,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating session main summary:', error);
    throw error;
  }
};

export const updatePhysicalExam = async (sessionId, physicalExamData) => {
  try {
    await updateDoc(doc(db, 'chatbot', sessionId), {
      bp_mm: physicalExamData.bp_mm || null,
      bp_hg: physicalExamData.bp_hg || null,
      heartrate: physicalExamData.heartrate || null,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating physical exam:', error);
    throw error;
  }
};

export const updateSessionDuration = async (sessionId, duration) => {
  try {
    await updateDoc(doc(db, 'chatbot', sessionId), {
      actual_duration: duration,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating session duration:', error);
    throw error;
  }
};

export const updatePatientStatus = async (sessionId, status) => {
  try {
    await updateDoc(doc(db, 'chatbot', sessionId), {
      patientstatus: status,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating patient status:', error);
    throw error;
  }
};

// ==================== SIGNUP REQUESTS ====================

export const getAllSignupRequests = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'signup_requests'));
    return querySnapshot.docs
      .map(d => serializeFirestoreData({ id: d.id, ...d.data() }))
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  } catch (error) {
    logger.error('Error getting signup requests:', error);
    throw error;
  }
};

export const getSignupRequestsByStatus = async (status) => {
  try {
    const q = query(collection(db, 'signup_requests'), where('status', '==', status));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(d => serializeFirestoreData({ id: d.id, ...d.data() }))
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  } catch (error) {
    logger.error('Error getting signup requests by status:', error);
    throw error;
  }
};

export const updateSignupRequest = async (requestId, data) => {
  try {
    await updateDoc(doc(db, 'signup_requests', requestId), {
      ...data,
      updated_at: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error('Error updating signup request:', error);
    throw error;
  }
};

// ==================== GI USERS (GI specialists) ====================

export const getGIUserByEmail = async (email) => {
  try {
    const q = query(collection(db, 'gi_users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    const giDoc = querySnapshot.docs[0];
    return { id: giDoc.id, ...giDoc.data() };
  } catch (error) {
    logger.error('Error getting GI user by email:', error);
    throw error;
  }
};

export const createGIUser = async (userData) => {
  try {
    const docRef = await addDoc(collection(db, 'gi_users'), {
      ...userData,
      created_on: new Date().toISOString()
    });
    await updateDoc(doc(db, 'gi_users', docRef.id), { user_id: docRef.id });
    return docRef.id;
  } catch (error) {
    logger.error('Error creating GI user:', error);
    throw error;
  }
};

export const getAllGIUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'gi_users'));
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    logger.error('Error getting all GI users:', error);
    throw error;
  }
};

export const updateGIUserStatus = async (userId, accountStatus) => {
  try {
    await updateDoc(doc(db, 'gi_users', userId), {
      accountStatus,
      status_updated_at: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    logger.error('Error updating GI user status:', error);
    throw error;
  }
};

// ==================== PCP NOTIFICATIONS (from GI Portal) ====================

/**
 * Real-time listener for PCP notifications.
 * Queries pcp_notifications by recipient_email (PCP's email).
 */
export const onPCPNotificationsSnapshot = (recipientEmail, callback, maxCount = 20) => {
  const q = query(
    collection(db, 'pcp_notifications'),
    where('recipient_email', '==', recipientEmail),
    orderBy('created_at', 'desc'),
    firestoreLimit(maxCount),
  );
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(list);
  });
};

/** Mark a single PCP notification as read */
export const markPCPNotificationRead = async (notifId) => {
  await updateDoc(doc(db, 'pcp_notifications', notifId), { read: true });
};

/** Batch mark all unread PCP notifications as read */
export const markAllPCPNotificationsRead = async (recipientEmail) => {
  const q = query(
    collection(db, 'pcp_notifications'),
    where('recipient_email', '==', recipientEmail),
    where('read', '==', false),
  );
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.update(d.ref, { read: true }));
  await batch.commit();
};

/** Create a notification for GI specialists (written to gi_notifications collection) */
export const createGINotification = async (data) => {
  const docRef = await addDoc(collection(db, 'gi_notifications'), {
    ...data,
    read: false,
    created_at: new Date().toISOString(),
  });
  return docRef.id;
};

// ==================== MA — PCP CASES FUNCTIONS ====================

/**
 * Fetch full details of a single pcp_case including about and health subcollections.
 * @param {string} caseId - pcp_cases document id
 */
export const getPCPCaseDetails = async (caseId) => {
  try {
    const caseRef = doc(db, 'pcp_cases', caseId);
    const aboutRef = doc(db, 'pcp_cases', caseId, 'about', 'data');
    const healthRef = doc(db, 'pcp_cases', caseId, 'health', 'data');
    const docsRef = collection(db, 'pcp_cases', caseId, 'documents');
    const pcpPharmacyRef = doc(db, 'pcp_pharmacy', caseId);

    const [caseSnap, aboutSnap, healthSnap, docsSnap, pcpPharmacySnap] = await Promise.all([
      getDoc(caseRef),
      getDoc(aboutRef),
      getDoc(healthRef),
      getDocs(docsRef),
      getDoc(pcpPharmacyRef),
    ]);

    if (!caseSnap.exists()) throw new Error('Case not found');

    // Only fetch metadata — exclude chunk sub-docs (too large)
    const documents = docsSnap.docs
      .filter(d => d.id !== 'chunks')
      .map(d => ({ id: d.id, ...d.data() }));

    const pcpPharmacy = pcpPharmacySnap.exists() ? pcpPharmacySnap.data() : null;

    return {
      id: caseId,
      ...caseSnap.data(),
      about: aboutSnap.exists() ? aboutSnap.data() : null,
      health: healthSnap.exists() ? healthSnap.data() : null,
      pcpPharmacy,
      documents,
    };
  } catch (error) {
    logger.error('Error fetching PCP case details:', error);
    throw error;
  }
};

/**
 * Fetch all PCP users from pcp_users collection.
 * Used to populate the PCP filter dropdown on MA's PCP Cases page.
 */
export const getAllPCPUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'pcp_users'));
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    logger.error('Error getting all PCP users:', error);
    throw error;
  }
};

/**
 * Fetch pcp_cases with client-side filtering and pagination.
 * @param {object} params
 * @param {number} params.page - current page (1-based)
 * @param {number} params.perPage - records per page
 * @param {string} params.searchText - search across patient name, shortCode, PCP name
 * @param {string} params.filterStatus - 'all' | 'draft' | 'submitted' | 'under_review' | 'completed' | 'closed'
 * @param {string} params.filterGI - 'all' | 'assigned' | 'unassigned'
 * @param {string} params.filterPCP - 'all' | pcp_users doc id
 * @param {Array}  params.pcpUsersMap - array of { id, name, email } from getAllPCPUsers (for name lookup)
 */
export const getPCPCasesWithFilters = async ({
  page = 1,
  perPage = 10,
  searchText = '',
  filterStatus = 'all',
  filterGI = 'all',
  filterPCP = 'all',
  pcpUsersMap = [],
  onlySharedWithMa = false,
} = {}) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'pcp_cases'));
    let allCases = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Filter: MA users only see cases where sharedWithMa === true
    if (onlySharedWithMa) {
      allCases = allCases.filter(c => c.sharedWithMa === true);
    }

    // Build a quick id->name map from pcpUsersMap
    const pcpNameMap = {};
    pcpUsersMap.forEach(u => {
      pcpNameMap[u.id] = u.name || u.fullName || u.email || u.id;
    });

    // Attach PCP display name to each case
    allCases = allCases.map(c => ({
      ...c,
      pcpUserName: pcpNameMap[c.ownerUserId] || '',
    }));

    // Filter: status
    if (filterStatus !== 'all') {
      allCases = allCases.filter(c => c.status === filterStatus);
    }

    // Filter: GI assignment
    if (filterGI === 'assigned') {
      allCases = allCases.filter(c => !!c.sharedWithGiUserId);
    } else if (filterGI === 'unassigned') {
      allCases = allCases.filter(c => !c.sharedWithGiUserId);
    }

    // Filter: specific PCP
    if (filterPCP !== 'all') {
      allCases = allCases.filter(c => c.ownerUserId === filterPCP);
    }

    // Filter: search text (patient name via title, shortCode, PCP name)
    if (searchText && searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      allCases = allCases.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.shortCode || '').toLowerCase().includes(q) ||
        (c.pcpUserName || '').toLowerCase().includes(q)
      );
    }

    // Sort by createdAt descending (newest first)
    allCases.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });

    const totalRecords = allCases.length;
    const totalPages = Math.ceil(totalRecords / perPage) || 1;
    const startIndex = (page - 1) * perPage;
    const paginatedCases = allCases.slice(startIndex, startIndex + perPage);

    return {
      cases: paginatedCases,
      total_pages: totalPages,
      total_records: totalRecords,
      current_page: page,
      per_page: perPage,
    };
  } catch (error) {
    logger.error('Error getting PCP cases with filters:', error);
    throw error;
  }
};

/**
 * Assign a GI specialist to a pcp_case.
 * Updates sharedWithGiUserId, sharedWithGiUser, sharedWithGiAt.
 * If case status is 'submitted', transitions it to 'under_review'.
 * Also creates an admin audit log entry.
 *
 * @param {string} caseId - pcp_cases document id
 * @param {object} giUser - { id, fullName, displayName, email }
 * @param {object} adminInfo - { adminId, adminEmail }
 */
export const assignGIToPCPCase = async (caseId, giUser, adminInfo = {}) => {
  try {
    const caseRef = doc(db, 'pcp_cases', caseId);
    const caseSnap = await getDoc(caseRef);
    if (!caseSnap.exists()) throw new Error('Case not found');

    const caseData = caseSnap.data();
    const now = new Date().toISOString();
    const giDisplayName = giUser.displayName || giUser.fullName || giUser.email || '';

    const updatePayload = {
      sharedWithGiUserId: giUser.id,
      sharedWithGiUser: giDisplayName,
      sharedWithGiAt: now,
      updatedAt: now,
      statusUpdatedAt: now,
    };

    // Transition status only if currently 'submitted'
    if (caseData.status === 'submitted') {
      updatePayload.status = 'under_review';
    }

    await updateDoc(caseRef, updatePayload);

    // Audit log
    await createAdminLog({
      action: 'ma_assigned_gi_to_pcp_case',
      case_id: caseId,
      admin_id: adminInfo.adminId || '',
      admin_email: adminInfo.adminEmail || '',
      assigned_gi_id: giUser.id,
      assigned_gi_name: giDisplayName,
      previous_status: caseData.status,
    });

    return true;
  } catch (error) {
    logger.error('Error assigning GI to PCP case:', error);
    throw error;
  }
};

/**
 * Assemble a PCP-portal document from Firestore base64 chunks and return a blob URL.
 * Chunks stored at: pcp_cases/{caseId}/documents/{fileId}/chunks/{0,1,...}
 * Each chunk doc: { data: "base64_string" }
 *
 * @param {string} caseId
 * @param {string} fileId
 * @param {number} chunkCount - total number of chunks (from document metadata)
 * @param {string} contentType - MIME type e.g. "image/jpeg", "application/pdf"
 * @returns {Promise<string>} objectURL — caller must revoke with URL.revokeObjectURL() when done
 */
export const loadPCPDocumentBlobUrl = async (caseId, fileId, chunkCount, contentType) => {
  try {
    const chunkPromises = [];
    for (let i = 0; i < chunkCount; i++) {
      chunkPromises.push(
        getDoc(doc(db, 'pcp_cases', caseId, 'documents', fileId, 'chunks', String(i)))
          .then(snap => {
            if (!snap.exists()) return '';
            const data = snap.data();
            return typeof data?.data === 'string' ? data.data : '';
          })
      );
    }
    const chunks = await Promise.all(chunkPromises);
    const base64 = chunks.join('');

    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    const blob = new Blob([bytes.buffer], { type: contentType || 'application/octet-stream' });
    return URL.createObjectURL(blob);
  } catch (error) {
    logger.error('Error loading PCP document blob URL:', error);
    throw error;
  }
};

/**
 * Fetch the GI shared report from gi_shared_reports collection.
 * Document ID equals the pcp_cases case ID.
 * @param {string} caseId
 * @returns {Promise<Object|null>}
 */
export const getGISharedReport = async (caseId) => {
  try {
    const snap = await getDoc(doc(db, 'gi_shared_reports', caseId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    logger.error('Error fetching GI shared report:', error);
    return null;
  }
};
